/**
 * Ligatures from the Material Symbols font — a different family with its own ligature names, not
 * interchangeable with the classic Material Icons in {@link MaterialIcon} below. `Icon.symbol` always renders
 * in this font; `Icon.view` auto-detects via {@link isMaterialSymbol}, so every symbol must live in this one
 * enum — a second declaration merges at the type level but is invisible to the value Set built below it.
 */
export enum MaterialSymbol {
	FIELD_MID_SPAN = 'format_line_spacing',
	LEFT_PANEL_CLOSE = 'left_panel_close',
	LEFT_PANEL_OPEN = 'left_panel_open',
}

const MATERIAL_SYMBOL_VALUES: ReadonlySet<string> = new Set<string>(Object.values(MaterialSymbol));

export function isMaterialSymbol(icon: unknown): icon is MaterialSymbol {
	return typeof icon === 'string' && MATERIAL_SYMBOL_VALUES.has(icon);
}

export enum MaterialIcon {
	ADD = 'add',
	ANNOUNCEMENT = 'announcement',
	ADD_LOCATION = 'add_location',
	ASSIGN = 'how_to_reg',
	BRIGHTNESS_HIGH = 'brightness_high',
	BRIGHTNESS_LOW = 'brightness_low',
	BRIGHTNESS_MEDIUM = 'brightness_medium',
	CHECK = 'check',
	CHECK_BOX = 'check_box_outline_blank',
	CHECK_BOX_INDETERMINATE = 'indeterminate_check_box',
	CHECK_BOX_SELECTED = 'check_box',
	CHECK_CIRCLE = 'check_circle_outline',
	CLOSE = 'close',
	CONTENT_COPY = 'content_copy',
	CONTENT_PASTE = 'content_paste',
	DATE = 'date_range',
	BEARING = 'nat',
	DASHBOARD = 'dashboard',
	DELETE = 'delete',
	DEPARTMENT = 'domain',
	DONE_ALL = 'done_all',
	DUPLICATE = 'file_copy',
	EDIT = 'mode_edit',
	EDIT_LOCATION = 'edit_location',
	EXIT_TO_APP = 'exit_to_app',
	FIELD_FORM = 'ballot',
	FIELD_HEIGHT = 'vertical_align_top',
	FIELD_IMAGE = 'photo_camera',
	FIELD_LOCATION = 'my_location',
	FIELD_NESTED_LIST = 'view_quilt',
	FIELD_NUMBER = 'looks_one',
	FIELD_ORIENTATION = 'explore',
	FIELD_SELECT_LIST = 'view_list',
	FIELD_TEXT = 'font_download',
	FIELD_TRUE_SIZE = 'camera',
	FIELD_VECTOR = 'directions',
	FIELD_LINE = 'polyline',
	FIELD_ASSET_TYPE = 'category',
	// Asset type icon picker choices (@see ASSET_TYPE_ICONS). WARNING above covers the 'warning' entry already.
	LIGHT_MODE = 'light_mode',
	CENTER_FOCUS_STRONG = 'center_focus_strong',
	SURROUND_SOUND = 'surround_sound',
	RECTANGLE = 'rectangle',
	HOURGLASS_TOP = 'hourglass_top',
	VIEW_STREAM = 'view_stream',
	STOP_CIRCLE = 'stop_circle',
	EJECT = 'eject',
	NETWORK_CELL = 'network_cell',
	DISABLED_BY_DEFAULT = 'disabled_by_default',
	FIRE_HYDRANT_ALT = 'fire_hydrant_alt',
	EMERGENCY = 'emergency',
	LOCAL_FIRE_DEPARTMENT = 'local_fire_department',
	PARK = 'park',
	HIGHLIGHT_OFF = 'highlight_off',
	PAUSE_PRESENTATION = 'pause_presentation',
	BORDER_ALL = 'border_all',
	HEXAGON = 'hexagon',
	SWIPE_DOWN_ALT = 'swipe_down_alt',
	HOME = 'home',
	VILLA = 'villa',
	VIEW_COLUMN = 'view_column',
	STAR = 'star',
	SQUARE = 'square',
	PENTAGON = 'pentagon',
	DETAILS = 'details',
	COMMIT = 'commit',
	TEXTURE = 'texture',
	PLACE = 'place',
	NATURE = 'nature',
	GAMES = 'games',
	API = 'api',
	ELECTRIC_BOLT = 'electric_bolt',
	INVERT_COLORS = 'invert_colors',
	SETTINGS_INPUT_ANTENNA = 'settings_input_antenna',
	WIFI_2_BAR = 'wifi_2_bar',
	SENSORS = 'sensors',
	FILE_DOWNLOAD = 'file_download',
	FILE_UPLOAD = 'file_upload',
	FILTER_NONE = 'filter_none',
	FOLDER = 'folder',
	FOLDER_OPEN = 'folder_open',
	GPS_FIXED = 'gps_fixed',
	GPS_OFF = 'gps_off',
	GPS_ON = 'gps_not_fixed',
	INBOX = 'inbox',
	HELP = 'help',
	HELP_OUTLINE = 'help_outline',
	INFO = 'info_outline',
	INFO_FILLED = 'info',
	KEY_DOWN = 'keyboard_arrow_down',
	KEY_LEFT = 'keyboard_arrow_left',
	KEY_RIGHT = 'keyboard_arrow_right',
	KEY_UP = 'keyboard_arrow_up',
	LASER = 'flare',
	HEIGHT = 'height',
	LINEAR_SCALE = 'linear_scale',
	LOCATION = 'location_on',
	LOCK = 'lock',
	MAGIC_WAND = 'auto_fix_high',
	MANAGE_HISTORY = 'manage_history',
	MAP = 'map',
	MASK = 'select_all',
	MORE_VERT = 'more_vert',
	MOVE = 'forward',
	MOVE_DOWN = 'move_down',
	MENU = 'menu',
	NAVIGATE_NEXT = 'navigate_next',
	NAVIGATE_PREV = 'navigate_before',
	NONE = '',
	ORG_SEARCH = 'youtube_searched_for',
	PERSON = 'person',
	PERSON_BLACK = 'person_black',
	PHOTO_MEASURE = 'straighten',
	POLE_BASE = 'vertical_align_bottom',
	PUBLIC = 'public',
	RADIO = 'radio_button_unchecked',
	RADIO_SELECTED = 'radio_button_checked',
	SAVE = 'save_alt',
	SEARCH = 'search',
	SELECT = 'call_made',
	SPARKLE = 'auto_awesome',
	MASK_BLUR = 'deblur',
	HIDE_PHOTO = 'hide_image',
	SHORT_TEXT = 'short_text',
	SORT = 'sort_by_alpha',
	SPAN_LINK = 'format_size',
	STATUS = 'flag',
	TAG = 'more',
	TAG_GROUP = 'group_work',
	UPDATE = 'update',
	VERTICAL_DISTRIBUTE = 'vertical_distribute',
	VERTICAL_SEPARATION = 'swap_vert',
	VISIBILITY_OFF = 'visibility_off',
	ZOOM_IN = 'zoom_in',
	ZOOM_OUT = 'zoom_out',
	ZOOM_OUT_MAP = 'zoom_out_map',
	SET_CROP = 'aspect_ratio',
	REMOVE = 'remove',
	SQUARE_FOOT = 'square_foot',
	FORM_UPDATE = 'assignment_turned_in',
	CLOUD_UPLOAD = 'cloud_upload',
	FIELD_ANCHOR = 'architecture',
	WARNING = 'warning',
	VISIBILITY = 'visibility',
	DESCRIPTION = 'description',
	PLAY_CIRCLE = 'play_circle',
	PAUSE_CIRCLE = 'pause_circle',
	SUPPORT = 'support',
	CONTACT_SUPPORT = 'contact_support',
	PRINT = 'print',
	OPEN_IN_NEW = 'open_in_new',
}
